import { useEffect } from "react";
import {
  X, Radio, Crosshair, Smile, Users, EyeOff, Keyboard, Radar,
} from "lucide-react";

/**
 * What-am-I-looking-at panel.
 *
 * There is a lot of mechanic in this app — marking windows, cull votes,
 * traces you have to physically walk to, emotes, proximity takeovers, an
 * entity that behaves differently from the rest — and none of it was ever
 * explained. New viewers saw a map with dots on it and a tab labelled TRACES
 * with a number next to it. This is the missing paragraph.
 *
 * Shown automatically on a first visit, and reachable from the top bar after
 * that. Deliberately written in the app's own voice: it describes what happens,
 * not how it is implemented.
 */
const SECTIONS = [
  {
    Icon: Radio,
    title: "The assets",
    body: (
      <>
        Assets appear red. <em>One does not.</em>
      </>
    ),
  },
  {
    Icon: Radar,
    title: "Traces",
    body: (
      <>
        When an asset stops somewhere it sometimes leaves a trace signal. They
        show on the map as faint rings — no names, because the point is to find
        the spot. Open <strong>TRACES</strong> for a compass view that gets
        louder and faster as you close in. Within five metres, hold to scan.
        First person there takes it, so if others are standing at the same spot
        it becomes a race.
      </>
    ),
  },
  {
    Icon: Crosshair,
    title: "Marking a spot",
    body: (
      <>
        For one minute in every ten you can drop a marker at the crosshair, once
        you have been watching for ten minutes. Everyone sees it, it stays for
        five, and assets sometimes come to investigate it. Occasionally a marker
        appears that nobody placed.
      </>
    ),
  },
  {
    Icon: Users,
    title: "The cull vote",
    body: (
      <>
        More than three markers on the map and everyone watching is asked to
        vote one off. It runs round by round until a single marker survives. Tap
        a name to see it before you decide.
      </>
    ),
  },
  {
    Icon: Smile,
    title: "Emotes",
    body: (
      <>
        The bar at the bottom broadcasts an icon to every viewer for a few
        seconds. Your position is fuzzed by a few hundred metres before it goes
        out, so it reads as "someone in your town" and no closer.
      </>
    ),
  },
  {
    Icon: EyeOff,
    title: "When one gets close",
    body: (
      <>
        Get near a moving asset and it may take over your view until the link
        decays. You can end it at any time by turning off your visibility, which
        is <strong>Privacy Mode</strong> in settings — it stops your location
        being shared and takes you off their radar. It stays off until you turn
        it back on.
      </>
    ),
  },
];

const KEYS = [
  ["F", "follow the tracked asset"],
  ["A", "activity feed"],
  ["T", "traces"],
  ["C", "centre on me"],
  ["M", "mark this spot"],
  ["Esc", "step back"],
];

export const Briefing = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="briefing-overlay"
      onClick={onClose}
      data-testid="briefing-overlay"
    >
      <div
        className="briefing"
        role="dialog"
        aria-modal="true"
        aria-label="What you're looking at"
        onClick={(e) => e.stopPropagation()}
        data-testid="briefing"
      >
        <div className="briefing-head">
          <div className="briefing-title">
            <span className="briefing-mark" />
            <span>WHAT YOU'RE LOOKING AT</span>
          </div>
          <button onClick={onClose} data-testid="briefing-close" aria-label="Close">
            <X size={13} />
          </button>
        </div>

        <div className="briefing-body">
          {SECTIONS.map(({ Icon, title, body }) => (
            <section className="briefing-sec" key={title}>
              <h3><Icon size={13} /> {title}</h3>
              <p>{body}</p>
            </section>
          ))}

          <section className="briefing-sec briefing-keys">
            <h3><Keyboard size={13} /> Shortcuts</h3>
            <dl>
              {KEYS.map(([key, what]) => (
                <div key={key}>
                  <dt>{key}</dt>
                  <dd>{what}</dd>
                </div>
              ))}
            </dl>
          </section>
        </div>

        <button className="briefing-done" onClick={onClose} data-testid="briefing-done">
          START WATCHING
        </button>
      </div>
    </div>
  );
};
